/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import getSubscriptions from 'api/subscriptions/getSubscriptions'
import createNewSubscription from 'api/subscriptions/postSubscription'
import {
	SubscriptionData,
	CreateSubscriptionBody,
	SubscriptionResponse,
} from 'types'
import handleAxiosError from 'utils/handleAxiosError'

interface SubscriptionState {
	records: SubscriptionData[]
	loading: boolean
	error: string | null
}

const initialState: SubscriptionState = {
	records: [],
	loading: false,
	error: null,
}

export const getSubscriptionsAction = createAsyncThunk<
	SubscriptionData[],
	void
>('subscription/getSubscriptionsAction', async (_, { rejectWithValue }) => {
	try {
		return await getSubscriptions()
	} catch (error: any) {
		handleAxiosError(error)
		return rejectWithValue(error.message)
	}
})

export const createSubscriptionAction = createAsyncThunk<
	SubscriptionResponse,
	CreateSubscriptionBody
>(
	'subscription/createSubscriptionAction',
	async (body, { rejectWithValue }) => {
		try {
			return await createNewSubscription(body)
		} catch (error: any) {
			handleAxiosError(error)
			return rejectWithValue(error.message)
		}
	}
)

export const subscriptionSlice = createSlice({
	name: 'subscription',
	initialState,
	reducers: {
		clearSubscriptions: state => {
			state.records = []
			state.error = null
			state.loading = false
		},
	},
	extraReducers: builder => {
		builder.addCase(getSubscriptionsAction.pending, state => {
			state.loading = true
			state.error = null
		})
		builder.addCase(getSubscriptionsAction.fulfilled, (state, action) => {
			state.loading = false
			state.records = action.payload
		})
		builder.addCase(getSubscriptionsAction.rejected, (state, action) => {
			state.loading = false
			state.error = action.payload as string
		})
		builder.addCase(createSubscriptionAction.pending, state => {
			state.loading = true
			state.error = null
		})
		builder.addCase(createSubscriptionAction.fulfilled, state => {
			state.loading = false
		})
		builder.addCase(createSubscriptionAction.rejected, (state, action) => {
			state.loading = false
			state.error = action.payload as string
		})
	},
})

export const { clearSubscriptions } = subscriptionSlice.actions
export default subscriptionSlice.reducer
